import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { api } from '../services/api';
import { theme } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { BarChart3 } from 'lucide-react';

const PIE_COLORS = ['#10B981', '#34D399', '#06B6D4', '#F59E0B', '#059669', '#EC4899'];

type AnalyticsData = {
  monthlyLeads: { month: string; month_key: string; leads: number }[];
  statusBreakdown: { status: string; count: number }[];
  dailyLeads: { date: string; count: number }[];
};

const tooltipStyle = {
  background: 'var(--bg3)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  color: 'var(--text)',
};

const axisTick = { fill: 'var(--muted)', fontSize: 10 };

export function AnalyticsPage() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void api
      .get<AnalyticsData>('/dashboard/analytics')
      .then((r) => setAnalytics(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!analytics) {
    return (
      <div className="odc-page relative z-10 odc-card max-w-lg p-10 text-center" style={{ transform: 'none' }}>
        <p style={{ color: 'var(--muted)' }}>No analytics data.</p>
      </div>
    );
  }

  const barData = analytics.dailyLeads.map((x) => ({
    ...x,
    label: x.date.slice(5),
  }));

  const monthlyData = analytics.monthlyLeads;
  const statusData = analytics.statusBreakdown.map((s) => ({ name: s.status, value: s.count }));
  const totalLeads = analytics.dailyLeads.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}>
            <BarChart3 className="h-3.5 w-3.5" style={{ color: theme.accent }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.accent }}>
              Insights
            </span>
          </div>
          <h1 className="mt-3 text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
            Analytics
          </h1>
          <p className="mt-1 max-w-lg text-sm" style={{ color: 'var(--muted)' }}>
            Funnel summary, lead velocity, sources, and platform performance — Recharts on ODC glass cards.
          </p>
        </div>
        <div className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
          Last 6 months + 30 day trend
        </div>
      </header>

      <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
        <p className="text-xs uppercase tracking-[0.15em]" style={{ color: 'var(--muted)' }}>
          Leads captured (last 30 days)
        </p>
        <p className="mt-2 text-3xl font-extrabold" style={{ color: '#ffffff' }}>
          {totalLeads.toLocaleString()}
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
          <h3 className="mb-1 font-bold" style={{ color: 'var(--text)' }}>
            Monthly leads
          </h3>
          <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
            Leads by month for the last 6 months.
          </p>
          <div className="h-64 w-full min-h-[240px]">
            {monthlyData.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No monthly leads yet.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                  <XAxis dataKey="month" tick={axisTick} axisLine={{ stroke: 'var(--border)' }} />
                  <YAxis tick={axisTick} axisLine={{ stroke: 'var(--border)' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--muted)' }}
                    cursor={{ fill: 'var(--card2)' }}
                  />
                  <Bar dataKey="leads" fill={theme.primary} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
          <h3 className="mb-1 font-bold" style={{ color: 'var(--text)' }}>
            Lead status breakdown
          </h3>
          <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
            New, contacted, converted, and lost distribution.
          </p>
          <div className="h-64 w-full min-h-[240px]">
            {statusData.length === 0 || statusData.every((p) => p.value === 0) ? (
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No lead status data yet.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={80}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {statusData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="var(--border)" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: 'var(--muted)' }}
                    formatter={(value) => <span style={{ color: 'var(--text)' }}>{value}</span>}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
        <h3 className="mb-1 font-bold" style={{ color: 'var(--text)' }}>
          Daily leads trend
        </h3>
        <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
          Lead movement over the last 30 days.
        </p>
        <div className="h-52 w-full min-h-[200px]">
          {barData.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              No data for area chart.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={barData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="odcLeadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={theme.primary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={theme.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: 'var(--border)' }} />
                <YAxis tick={axisTick} axisLine={{ stroke: 'var(--border)' }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke={theme.primary} fill="url(#odcLeadGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
